import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table.tsx';
import { Toggle } from './components/ui/toggle'
import BaseSystemView from './views/BaseSystemView';
import ExpansionSystemView from './views/ExpansionSystemView';
import ExecPricingView from './views/ExecPricingView';

import { CCI_FEATURE_ENABLED, cciBlockedByDualCpuReason } from './utils/shouldUseCciPath';
import { buildHardwareReferenceRows } from './utils/hardwareReference';
// NOTE: pricingCalculator.jsx now re-exports getAllActivePartNumbers per the
// task-9 brief, but that function returns a nested { nodes, drives, network,
// software, services } object, not an iterable — `new Set(...)` on it throws.
// getAllUniquePartNumbers flattens that structure to the array of SKU strings
// ExecPricingView's `activePartNumbers.has(sku)` needs; it's pulled straight
// from partNumberUtils.jsx like generateBom.jsx/pricingCalculator.jsx already
// do. See task-9-report.md for the deviation from the brief's literal
// `getAllActivePartNumbers(...)` call.
import { getAllUniquePartNumbers } from './utils/partNumberUtils.jsx';
import { useExecPricing, useSavedPricePin } from './hooks/useExecPricing';
import { useExecConfirmDialog } from './components/ExecConfirmDialog';
import RepriceDialog from './components/RepriceDialog';
import SpecialPricingMetrics from './components/SpecialPricingMetrics';
import { buildPersistEvents, recordPersistEvents, actorFromUser } from './utils/execPricingAudit';
import { applyOverrides } from './pricing/applyOverrides';
import { buildRepriceCandidate } from './pricing/repriceCandidate';
import { fetchCatalogRows } from './utils/catalogFetch';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from '@aws-amplify/auth';
import awsconfig from './amplifyconfiguration.json';
import _ from 'lodash';

// New Authentication imports - no longer using Amplify's prebuilt components
import { getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import '@aws-amplify/ui-react/styles.css';
import './styles/configurator.css';
import { VERSION, RECENT_CHANGES } from './version';
import QuotesModal from './QuotesModal';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { adminRoutes } from './admin/adminRoutes';
import { LicenseGate } from './app/LicenseGate';
import { LoginCard } from './app/auth/LoginCard';
import IdleSessionGuard from './auth/IdleSessionGuard';
import { AppShell } from './app/v2/AppShell';
import { TryNewLookButton } from './app/entry/TryNewLookButton';
import DevKit from './app/dev/DevKit';

// Hooks
import { useConfigurationLogic } from './hooks/useConfigurationLogic.jsx';
import { useExecMetrics } from './hooks/useExecMetrics';
import { useDisplayMetrics } from './hooks/useDisplayMetrics';
import { useUserPermissions } from './hooks/useUserPermissions.jsx';
import { useVendor } from './hooks/useVendor';
import { usePriceEditions, shouldPromptForNewEdition } from './hooks/usePriceEditions';
import { priceBookFromEdition } from './pricing/priceBookFromEdition';
import { createPriceResolver } from './pricing/priceResolver';
import { useToast } from './hooks/useToast';
import { ToastContainer } from './components/ToastContainer';
// CONF-140 Phase 0b: the extracted classic-shell hooks and components.
import { useSpecialPricingInputs } from './hooks/useSpecialPricingInputs';
import { useConfigPersistence, EMPTY_CUSTOM_PRICES } from './hooks/useConfigPersistence';
import { useQuoteActions } from './hooks/useQuoteActions';
import { useQuoteValidation, validateQuoteField } from './hooks/useQuoteValidation';
import SaveQuoteDialog from './components/SaveQuoteDialog';
import BusyOverlay from './components/BusyOverlay';
import { LICENSE_STORAGE_KEY } from './app/LicenseGate';
// Vendor Registry
import { getVendor } from './config/vendorRegistry';
import { ConfiguratorGate } from './home/ConfiguratorGate';


import { calculateDefaultExpirationDate } from './utils/configUtilities';
import { deriveEffectiveConsultationMode, deriveInitialConsultationMode } from './utils/consultationView';
import ExportToS3Dialog from './utils/ExportToS3Dialog.jsx';

// CONSTANTS and Product Database Imports
import { getAvailableQuarters, getCurrentQuarter } from './constants/quarterConfig';
import { useAccountExecutives, DEV_BUILD_SHOWS_TEST_EXECS } from './hooks/useAccountExecutives';
import { log } from './utils/devLog';


Amplify.configure(awsconfig);

//************************************************************************************
// Authentication Wrapper Component
//************************************************************************************
const AuthWrapper = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthState();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      log('Hub event:', payload.event, payload.data);

      switch (payload.event) {
        case 'signInWithRedirect':
          log('Redirect sign-in initiated');
          break;
        case 'signInWithRedirect_failure':
          log('Redirect sign-in failed:', payload.data);
          setError('Sign in failed. Please try again.');
          break;
        case 'signedIn':
          log('User signed in successfully');
          // Add a small delay before checking auth state
          setTimeout(() => checkAuthState(), 1000);
          break;
        case 'signedOut':
          log('User signed out');
          setUser(null);
          break;
        case 'tokenRefresh':
          log('Token refreshed');
          break;
        case 'tokenRefresh_failure':
          log('Token refresh failed');
          setError('Session expired. Please sign in again.');
          break;
        default:
          log('Unhandled auth event:', payload.event);
          break;
      }
    });

    return unsubscribe;
  }, []);

  async function checkAuthState() {
    try {
      setLoading(true);
      setError(null);

      // Add debugging
      log('Checking auth state...');

      // Try to get session first
      const session = await fetchAuthSession();
      // log('Session:', session);

      if (session.tokens) {
        log('Tokens found, getting user...');
        const currentUser = await getCurrentUser();
        // Surface the user's real email for display. For SAML-federated users
        // (oktalogin, ChanneltivityAD) Cognito prepends the provider name to
        // the username, so we read the true email from the idToken's `email`
        // claim (requires the `email` OAuth scope, which is configured).
        // NOTE: fetchUserAttributes() is intentionally NOT used — it calls the
        // Cognito GetUser API which needs the aws.cognito.signin.user.admin
        // scope that our access token does not carry.
        const email = session.tokens.idToken?.payload?.email;
        setUser({ ...currentUser, email });
        // log('Current user:', currentUser);
      } else {
        log('No tokens found');
        setUser(null);
      }
    } catch (err) {
      log('Auth check error:', err);

      // Try alternative method
      try {
        log('Trying alternative auth check...');
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          log('Found tokens, but getCurrentUser failed');
          // Set a basic user object from token
          const userInfo = session.tokens.idToken?.payload;
          setUser({
            username: userInfo?.sub,
            email: userInfo?.email,
            attributes: userInfo
          });
        } else {
          setUser(null);
        }
      } catch (altErr) {
        log('Alternative auth check also failed:', altErr);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSignIn() {
    try {
      setError(null);
      // Amplify v6 way - redirects to hosted UI with all configured providers
      await signInWithRedirect();
    } catch (error) {
      console.error('Error signing in:', error);
      setError('Failed to sign in. Please try again.');
    }
  }

  async function handleSignOut() {
    try {
      // RevokeToken (which this plain signOut() calls) is the server-side control; global: true is
      // deliberately not used (ASVS L2 program spec §4a(a), docs/security/admin-global-sign-out.md).
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    }
  }

  // Loading state - matches your app's black theme
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login with matching theme
  if (!user) {
    return <LoginCard error={error} onSignIn={handleSignIn} />;
  }

  // Authenticated - render the main app with user prop
  return (
    <>
      <IdleSessionGuard onSignOut={handleSignOut} onSessionGone={() => setUser(null)} />
      {children({ user, signOut: handleSignOut })}
    </>
  );
};

//************************************************************************************
// Storage Configurator App component
//************************************************************************************
// initialVendorId comes from ConfiguratorGate: the user's first accessible
// vendor. It must be right on the FIRST render — useConfigurationLogic captures
// getInitialConfig(productDb, vendor.id) once at mount, and only user-driven
// switches (handleVendorChange) reconcile config afterwards.
export const StorageConfiguratorApp = ({ user, signOut, initialVendorId }) => {
  // Add this at the very beginning
  const renderCount = useRef(0);
  renderCount.current++;
  log(`🔄 StorageConfiguratorApp render #${renderCount.current}`);
  
  
  const [activeView, setActiveView] = useState('BaseSystem'); // 'BaseSystem' | 'Expansion'
  const [showExecPricing, setShowExecPricing] = useState(false);

  const permissions = useUserPermissions(user);
  const { userGroups, hasSpecialAccess, isCheckingAccess, licenseCheckComplete, hasFullConfigurator, canToggleConsultation, hasAdminAccess, hasExecAccess, hasDualCpuAccess } = permissions;
  const { toasts, showToast, dismissToast } = useToast();
  // CONF-177: the roster is admin-editable data now, not a bundled JSON file.
  // The hook falls back to that bundle whenever the API cannot answer, so this
  // list is never empty — an empty one would block quote generation outright.
  //
  // Test-only AEs are offered to admins in EVERY build, not just dev ones: an
  // admin generating a throwaway quote needs a name that is not a real
  // colleague's, and the old build-env gate could not grant that in production.
  // Reps without adminAccess never see them, so a customer quote cannot pick up
  // a placeholder name by mis-click. hasAdminAccess starts false and flips once
  // the session resolves; the list just gains those entries at that point.
  const { executives: accountExecutives } = useAccountExecutives({
    includeTest: hasAdminAccess || DEV_BUILD_SHOWS_TEST_EXECS,
  });
  const { dialogElement: execConfirmElement, confirmExecEscape } = useExecConfirmDialog();
  log('Permissions:', permissions);
  
  // Get the Hardware Vendor Details and DB info
  const {
    vendor,
    theme,
    productDb,
    metricsDb,
    switchVendor,
    canSwitchTo,
    getVendorConfigOverrides,
    accessibleVendorIds,
    hasMultipleVendors,
  } = useVendor({
    userGroups: permissions.userGroups,
    isCheckingAccess: permissions.isCheckingAccess,
    initialVendor: initialVendorId,
  });

  // CONF-106 project C: prices come from the published edition, not productDb.
  // Data-gated per vendor — priceBookFromEdition(null, productDb) returns
  // productDb unchanged, so a vendor with no edition behaves exactly as before.
  // `priceBook` is what every pricing consumer must receive; raw `productDb` is
  // now spec-sheet data only.
  const priceEditions = usePriceEditions(vendor.id);
  const priceBook = useMemo(
    () => priceBookFromEdition(priceEditions.edition, productDb),
    [priceEditions.edition, productDb],
  );

  const softwareSubscriptionOnly = vendor?.businessRules?.softwareSubscriptionOnly === true;

  log("VENDOR Information", vendor);

  // CONF-106 pt2: exec Custom HW Pricing overrides, mirrored into a plain
  // useState so they can feed useConfigurationLogic's `customPrices` option.
  //
  // Hook-order cycle & how it's broken: useConfigurationLogic needs
  // customPrices as an input (to compute config/pricing/bom with overrides
  // applied), but the value that would produce it — useExecPricing — needs
  // config.quarter, which is only available AFTER useConfigurationLogic runs.
  // Neither hook can be called first with the other's live output in the same
  // render pass. Pattern used: useState mirror (one-render lag).
  // useConfigurationLogic is called with the customPrices *state* (last
  // committed value, not the current-render execPricing.customPrices).
  // useExecPricing is then called with the fresh config.quarter it produced.
  // An effect copies execPricing.customPrices into that state whenever the
  // (memoized) reference changes, scheduling a follow-up render where
  // useConfigurationLogic picks up the new overrides. execPricing.customPrices
  // is already useMemo'd inside useExecPricing, so the mirrored state only
  // actually changes (and only re-triggers the debounced calc effect in
  // useConfigurationLogic) when overrides actually change.
  const [customPrices, setCustomPrices] = useState(EMPTY_CUSTOM_PRICES);

  // Use the new consolidated configuration hook
  const {
    config,
    system,
    pricing,
    viewState,
    metadata,
    metrics,
    bom,
    pricingData,
    baselinePricing,
    isCalculating,
    updateConfig: updateConfigRaw,
    batchUpdateConfig: batchUpdateConfigRaw,
    loadConfig: loadConfigRaw
  } = useConfigurationLogic(priceBook, metricsDb, vendor, { customPrices });

  // CONF-212: exec metric overrides sit AFTER pricing/BOM. `metrics` stays the
  // real figure for every pricing consumer; `displayMetrics` is what the cards,
  // PDFs, PPTX, CSV and the saved quote show. Not part of useConfigurationLogic
  // on purpose (spec §3).
  const execMetrics = useExecMetrics({
    enabledSets: { afa: !!config.afaEnabled, hybrid: !!config.hybridEnabled },
    throughputBasis: metrics?.throughputBasis,
    onLegacyConverted: (message) => showToast({ badge: 'CUSTOM METRICS', title: 'Custom metrics updated', message }),
  });
  const [showExecMetrics, setShowExecMetrics] = useState(false);
  const { displayMetrics, affectedKeys, staleKeys } = useDisplayMetrics(
    metrics, execMetrics.overrides, { isPBUnit: config.isPBUnit });

  // ONE resolver for every pricing consumer below. `priceBook`, never raw
  // productDb — this file's own rule at :467-470. `customPrices` is the
  // committed state mirror (:501), not execPricing.customPrices, so the
  // resolver changes only when the overrides are actually committed.
  const prices = useMemo(
    () => createPriceResolver(priceBook, config.quarter, customPrices),
    [priceBook, config.quarter, customPrices],
  );

  // CONF-106 project C: "work in progress" for edition revalidation. Same latch
  // idiom as execPricingTouched deliberately — one way to say "the user has done
  // something", not two. MUST flip on user-originated changes only: the initial
  // config computation and a vendor switch are not work in progress, and a latch
  // that flipped on them would banner every user on every publish.
  const configTouched = useRef(false);
  // Last edition number shown to the user (adopted silently or already
  // banner-acknowledged). Compared against priceEditions.editionNumber by
  // shouldPromptForNewEdition to detect a NEW edition landing while the
  // configurator is open.
  const shownEditionRef = useRef(null);

  // Thin wrappers around the raw updateConfig/batchUpdateConfig/loadConfig so
  // every one of the ~25 existing call sites (including the ones passed as
  // props to child components) latches configTouched automatically with no
  // further edits. useCallback keeps identities stable for those child props.
  // Exception: the vendor-switch call in handleVendorChange goes through
  // batchUpdateConfigRaw directly — see the comment there.
  const updateConfig = useCallback((...args) => {
    configTouched.current = true;
    return updateConfigRaw(...args);
  }, [updateConfigRaw]);
  const batchUpdateConfig = useCallback((...args) => {
    configTouched.current = true;
    return batchUpdateConfigRaw(...args);
  }, [batchUpdateConfigRaw]);
  const loadConfig = useCallback((...args) => {
    configTouched.current = true;
    return loadConfigRaw(...args);
  }, [loadConfigRaw]);

  // Adopts a newly-fetched edition silently once shownEditionRef has caught up
  // (pristine configurator), or records the FIRST edition seen this session so
  // there is a `previous` for shouldPromptForNewEdition to compare against —
  // without this branch, a rep who touches the config before the very first
  // edition load would spuriously get banner-prompted with previous === null.
  useEffect(() => {
    if (priceEditions.editionNumber === null) return;
    if (!configTouched.current) shownEditionRef.current = priceEditions.editionNumber;
    else if (shownEditionRef.current === null) shownEditionRef.current = priceEditions.editionNumber;
  }, [priceEditions.editionNumber]);

  // Review fix (post-task-11): vendor-scope shownEditionRef. usePriceEditions
  // resets editionNumber to null on every vendor change while the new
  // vendor's fetch is in flight (usePriceEditions.js:94-97), then settles on
  // THAT vendor's own edition-number sequence — unrelated to the old
  // vendor's numbering. Without this reset, a touched configurator leaves
  // shownEditionRef frozen at the old vendor's number (the adoption effect
  // above only overwrites it when pristine or still null), so vendor B's
  // numbers would almost never coincide with it and the banner would fire
  // indefinitely for the rest of the session — its only affordance is a full
  // reload, which would discard whatever the rep has configured for vendor B.
  // Resetting to null here makes vendor B's first real edition read as that
  // vendor's first load (previous === null), same as a fresh session.
  //
  // configTouched is deliberately NOT reset here. It's a session-wide "has
  // anything been done that a silent reprice could blindside" latch, same
  // monotonic idiom as execPricingTouched (never resets once true). A vendor
  // switch clears vendor-specific hardware selections via
  // batchUpdateConfigRaw's overrides, but metadata fields (opportunityID,
  // notes, quoteDescription, preparedFor, salesforceQuoteNumber, partnerName,
  // warranty/expiration dates) survive the switch untouched — real
  // unsaved work the rep would still lose on a forced reload. Resetting the
  // latch here would risk silently adopting a real edition change without
  // ever surfacing the banner for that surviving work; leaving it monotonic
  // only risks one extra (correct, not false) prompt later in the vendor-B
  // session. The false-banner bug this fixes was shownEditionRef being
  // stale, not configTouched being set — vendor-scoping the ref alone
  // resolves it.
  useEffect(() => {
    shownEditionRef.current = null;
  }, [vendor.id]);

  // Mirror the vendor class on <body> so vendor CSS reaches portaled UI
  // (Radix SelectContent mounts on document.body, outside the app div).
  useEffect(() => {
    const vendorClass = `vendor-${vendor.id.toLowerCase()}`;
    document.body.classList.add(vendorClass);
    return () => document.body.classList.remove(vendorClass);
  }, [vendor]);

  // Keeps customPrices (and thus the fetched catalog snapshot) alive after
  // the user collapses the Custom HW Pricing disclosure in the Bill of
  // Materials card, per the brief: only flips true the first time the
  // disclosure is opened, never resets.
  const execPricingTouched = useRef(false);
  // Tracks the quoteId of the last custom-priced quote loaded (null on clean
  // loads / not yet loaded). Consumed by a later reprice task; just the ref
  // is wired here.
  const loadedQuoteIdRef = useRef(null);
  const execPricing = useExecPricing({
    enabled: showExecPricing || execPricingTouched.current,
    vendor: vendor.id,
    quarter: config?.quarter,
    asOf: priceEditions.edition?.asOf,
    onOverridesReset: (reason) => showToast({ badge: 'EXEC PRICING', title: 'Custom pricing reset', message: reason }),
  });

  // Mirrors execPricing.customPrices into the customPrices state (hook-order
  // note above), with the part-3 saved-price pin: while a loaded custom-priced
  // quote is pinned and the exec session holds no overrides, the mirror is
  // suppressed so enable-fetch/reset churn cannot clobber the saved prices.
  // The pin releases on hydrate (session restored) or a fresh exec edit; see
  // useSavedPricePin in hooks/useExecPricing.js.
  const { pinSavedPrices, releasePin } = useSavedPricePin(execPricing, setCustomPrices);

  // UI-only state that stays in the component
  const [consultationMode, setConsultationMode] = useState(true);
  const effectiveConsultationMode = deriveEffectiveConsultationMode(consultationMode, canToggleConsultation);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const { quoteErrors, setQuoteErrors, setFieldError } = useQuoteValidation();

  // Display the true email (from the Cognito email attribute) rather than the
  // SAML-federated username (e.g. oktalogin_username@example.com). Falls back to
  // username only if the email attribute is unavailable.
  const userName = user.email || user.attributes?.email || user.username;

  // CONF-140 Phase 0b: extracted hooks. Order matters - persistence's onLoaded
  // needs the pricing-inputs setter, and quote actions need everything else.
  const spInputs = useSpecialPricingInputs({
    data: { pricing, viewState, config, vendor },
    config: { updateConfig },
  });
  const {
    tempSsdSoftware, tempHddSoftware, tempDiscountMonths,
    handleSsdSoftwareChange, handleSsdSoftwareBlur, handleHddSoftwareChange, handleHddSoftwareBlur,
    handleDiscountMonthsChange, handleDiscountMonthsBlur, handleRegToggle, swModel, swModelMonths,
  } = spInputs;

  const persistence = useConfigPersistence({
    data: { vendor, productDb: priceBook },
    access: { hasSpecialAccess, hasExecAccess },
    exec: { execPricing, pinSavedPrices, releasePin, setCustomPrices, loadedQuoteIdRef, execPricingTouched, execMetrics },
    config: { loadConfig, switchVendor, setActiveView },
    ui: { showToast, onLoaded: spInputs.applyLoadedSoftwarePrices },
  });
  const { loadSavedConfig, showSaveDialog, setShowSaveDialog, showQuotesModal, setShowQuotesModal } = persistence;

  const quoteActions = useQuoteActions({
    data: { config, system, metadata, metrics, displayMetrics, bom, pricingData, priceBook, productDb, prices, vendor },
    access: { hasSpecialAccess, user, userName },
    exec: { execPricing, execMetrics, confirmExecEscape },
    config: { updateConfig, batchUpdateConfig },
    ui: { showToast, setQuoteErrors, setShowExportDialog, setShowSaveDialog, validateQuoteField },
  });
  const {
    saveQuote, handleGenerateQuotePDF, handleGenerateSystemPDF, handleGenerateRackSlide, execEscapeActive, logEscape, busy,
  } = quoteActions;

  // Reprice-vs-current-catalog (CONF-106 part 3, spec §2.5): fetch the latest
  // catalog, diff it against the session's frozen basis (with the exec's
  // overrides re-applied on both sides), and let the exec accept/cancel.
  const [repriceDiff, setRepriceDiff] = useState(null);

  // BOM quantities by part number — the reprice dialog extends each unit delta
  // by quantity to show quote impact. Summed (not last-wins) so a part carried
  // on more than one BOM row reports its true quote quantity, matching how
  // buildPersistEvents scopes the audit trail.
  const qtyByPartNumber = useMemo(() => {
    const q = {};
    for (const row of bom || []) {
      if (row.partNumber) q[row.partNumber] = (q[row.partNumber] ?? 0) + (row.quantity ?? 0);
    }
    return q;
  }, [bom]);

  const startReprice = async () => {
    try {
      // asOf: the LIVE edition's asOf, not the frozen basis's own — reprice
      // answers "what would this cost against TODAY's published price book,"
      // the same cutoff useExecPricing's own live fetch uses. See
      // buildRepriceCandidate's doc for the full reasoning.
      const { candidate, diff } = await buildRepriceCandidate(
        fetchCatalogRows, execPricing.snapshot, execPricing.overrides, priceEditions.edition?.asOf);
      // The asOf this candidate was resolved against travels ON the candidate
      // (candidate.resolvedAsOf) — nothing to carry alongside it here. The
      // dialog can sit open for a while, so priceEditions.edition may have
      // moved by the time acceptReprice runs; rebase() reads the candidate's
      // ORIGINAL cutoff, not whatever asOf happens to be live at accept time.
      setRepriceDiff({ candidate, diff });
    } catch (e) {
      console.error('Reprice catalog fetch failed:', e);
      showToast({
        badge: 'EXEC PRICING', title: 'Reprice unavailable',
        message: 'Could not fetch the current catalog — reprice unavailable.',
      });
    }
  };

  const acceptReprice = async () => {
    const { candidate } = repriceDiff;
    // Per-line audit on ACCEPT (spec §2.4/§2.5), fail-closed: preview the
    // post-rebase session for the event pairs.
    const nextPrices = applyOverrides(candidate, execPricing.overrides).customPriceDollars;
    const events = buildPersistEvents('reprice', {
      execPricing: { ...execPricing, snapshot: candidate, customPrices: nextPrices },
      config, productDb: priceBook, bom,
      quoteSerial: loadedQuoteIdRef.current ?? null,
      actor: actorFromUser(user),
      vendorId: vendor.id, appVersion: VERSION,
    });
    if (!(await recordPersistEvents(events))) {
      showToast({
        badge: 'EXEC PRICING', title: 'Audit log failed',
        message: 'Could not record the reprice audit trail — reprice not applied.',
      });
      return;
    }
    execPricing.rebase(candidate);
    setRepriceDiff(null);
  };


  // Destructure values from the selected product database
  const {
    veloSsdCapacities,
    hybridVpodSsdCapacities,
    afaVpodSsdCapacities,
    hddCapacities,
    jbodSizes,
    compressionRatio,
    ssdPerVelo,
    ssdPerVpod
  } = productDb;

  // Capture the Rack Image offscreen to generate an image for the powerpoint
  const rackCaptureRef = useRef(null);

  // MORK TEST DEBUG
  // After permissions load, test vendor access  - MORK TODO : this is just a test for now
  // useEffect(() => {
  //   if (!permissions.isCheckingAccess) {
  //     log('🔍 DEBUG: Vendor Access Check');
  //     log('User Groups:', permissions.userGroups);
  //     log('');
      
  //     // Check each vendor's cognito group requirement
  //     Object.values(VENDORS).forEach(vendor => {
  //       log(`Vendor: ${vendor.id}`);
  //       log(`  Required Group: "${vendor.metadata.cognitoGroup}"`);
  //       log(`  Has Access:`, hasVendorAccess(vendor.id, permissions.userGroups));
  //     });
      
  //     log('');
  //     log('Accessible Vendors:', getAccessibleVendors(permissions.userGroups));
  //   }
  // }, [permissions.isCheckingAccess, permissions.userGroups]);


  // ============================================================================
  // 🧪 MORK TEST TODO TEMPORARY TEST: useVendor Hook
  // Remove this block after testing
  // ============================================================================
  // const vendorHookTest = useVendor({ userGroups: permissions.userGroups });

  // useEffect(() => {
  //   // Only log after permissions are loaded
  //   if (!isCheckingAccess) {
  //     log('');
  //     log('🧪 ========== useVendor Hook Test ==========');
  //     log('🏢 Current Vendor ID:', vendorHookTest.vendorId);
  //     log('📛 Display Name:', vendorHookTest.vendor.displayName);
  //     log('📋 Accessible Vendors:', vendorHookTest.accessibleVendorIds);
  //     log('👥 Has Multiple Vendors:', vendorHookTest.hasMultipleVendors);
  //     log('');
  //     log('🎨 Theme Sample:');
  //     log('   Card Header:', vendorHookTest.theme.cardHeader);
  //     log('   Button:', vendorHookTest.theme.button);
  //     log('   Table Header:', vendorHookTest.theme.tableHeader);
  //     log('');
  //     log('⚙️ Capabilities:');
  //     log('   Default JBOD:', vendorHookTest.capabilities.defaultJbodSize);
  //     log('   Available JBODs:', vendorHookTest.capabilities.availableJbodSizes);
  //     log('   Supports Hybrid:', vendorHookTest.capabilities.supportsHybrid);
  //     log('   Supports AFA:', vendorHookTest.capabilities.supportsAFA);
  //     log('   Supports E2E:', vendorHookTest.capabilities.supportsE2E);
  //     log('');
  //     log('📏 Business Rules:');
  //     log('   Force Consultation Mode:', vendorHookTest.rules.forceConsultationMode);
  //     log('   Default Compression:', vendorHookTest.rules.defaultCompressionRatio);
  //     log('   Min Subscription:', vendorHookTest.rules.minSubscriptionMonths, 'months');
  //     log('');
  //     log('💾 Databases:');
  //     log('   Product DB loaded:', !!vendorHookTest.productDb);
  //     log('   Metrics DB loaded:', !!vendorHookTest.metricsDb);
  //     log('   Product count:', Object.keys(vendorHookTest.productDb?.products || {}).length);
  //     log('========================================');
  //     log('');
  //   }
  // }, [isCheckingAccess, vendorHookTest.vendorId]);



  // Update the handleSignOut function:
  const handleSignOut = () => {
    try {
      localStorage.removeItem(LICENSE_STORAGE_KEY);
    } catch (error) {
      sessionStorage.removeItem(LICENSE_STORAGE_KEY);
    }
    signOut();
  };



  //************************************************************************************
  // Special function handlers                                                 
  //************************************************************************************

  // Ensure JBOD config is valid
  const handleJbodSizeChange = (value) => {
    const newJbodSize = parseInt(value);
    updateConfig({ jbodSize: newJbodSize });
  };

  const handleAccountExecutiveChange = (value) => {
    const selectedExec = accountExecutives.find(exec => exec.name === value);
    if (selectedExec) {
      batchUpdateConfig({
        accountExecutive: selectedExec.name,
        accountExecutiveEmail: selectedExec.email
      });
    }
  };

const handleVendorChange = (newVendorId) => {
  // Check access using hook's built-in method
  if (!canSwitchTo(newVendorId)) {
    log(`You don't have access to ${newVendorId} hardware configuration`);
    return;
  }

  log(`Switching from ${vendor.id} to ${newVendorId}`);

  // Switch vendor (updates vendor, theme, productDb, metricsDb internally)
  const success = switchVendor(newVendorId);

  if (!success) {
    console.error('Vendor switch failed');
    return;
  }

  // CONF-212 review fix (C1): overrides are absolute per-system numbers, so a
  // USER-driven vendor switch must drop them — but this must NOT be an effect
  // keyed on vendor.id. useConfigPersistence.loadSavedConfig also calls
  // switchVendor (for a quote saved under a different vendor) and then
  // execMetrics.hydrate(...) in the same batched click; an effect on vendor.id
  // would fire after that commit and wipe the overrides hydrate just set.
  // Resetting here, only on the explicit user action, avoids that race.
  execMetrics.resetAll();

  // Get vendor-specific config overrides from the hook
  const configOverrides = getVendorConfigOverrides(newVendorId);
  log("OVERRIDES", configOverrides);

  // Update local consultation mode state immediately based on new vendor
  // This ensures UI cards hide/show correctly before next render
  const newVendorConfig = getVendor(newVendorId);
  setConsultationMode(deriveInitialConsultationMode(
    canToggleConsultation, newVendorConfig.businessRules.forceConsultationMode));

  // Apply all vendor defaults at once. Goes through the RAW setter, not the
  // configTouched-latching wrapper: applying vendor defaults on a switch is
  // not user work in progress, and latching here would banner every rep on
  // every publish (CONF-106 project C task 11).
  batchUpdateConfigRaw(configOverrides);
};

  // Apply Dell or AIC styling to elements
  // MORK DELETE
  // const getStylesByVendor = () => {
  //   if (hardwareVendor === 'DELL') {
  //     return {
  //       cardHeader: "bg-blue-800",
  //       cardTitle: "text-xl font-bold text-white",
  //       button: "bg-green-500 hover:bg-blue-600 text-white",
  //       accentColor: "bg-blue-500",
  //       accentHover: "hover:bg-blue-400",
  //       selectTrigger: "hover:bg-gray-100/50",
  //       selectItem: "text-white hover:bg-gray-100/50",
  //       inputField: "border border-gray-300 hover:bg-gray-100/50 bg-black text-white",
  //       toggleActive: "bg-blue-500",
  //       toggleInactive: "bg-gray-700",
  //       logo: dellLogo,
  //       toggleDot: "bg-white",
  //       tableHeader: "bg-blue-800 text-center text-white font-bold",
  //       tableRow: "hover:bg-gray-100/40",
  //       saveButton: "bg-blue-500 hover:bg-blue-600 text-white",
  //       loadButton: "bg-green-500 hover:bg-green-600 text-white",
  //       summaryButton: "bg-blue-500 hover:bg-blue-600 text-white",
  //       quoteButton: "bg-red-500 hover:bg-red-600 text-white"
  //     };
  //   } else {
  //     return {
  //       cardHeader: "bg-vduraColor",
  //       cardTitle: "text-xl font-bold text-gray-800",
  //       button: "bg-orange-500 hover:bg-orange-600 text-white",
  //       accentColor: "bg-orange-500",
  //       accentHover: "hover:bg-orange-400",
  //       selectTrigger: "hover:bg-gray-100/50",
  //       selectItem: "text-white hover:bg-gray-100/50",
  //       inputField: "border border-gray-300 hover:bg-gray-100/50 bg-black text-white",
  //       toggleActive: "bg-orange-500",
  //       toggleInactive: "bg-gray-700",
  //       logo: aicLogo,
  //       toggleDot: "bg-white",
  //       tableHeader: "bg-vduraColor text-center text-black",
  //       tableRow: "hover:bg-gray-100/40",
  //       saveButton: "bg-blue-500 hover:bg-blue-600 text-white",
  //       loadButton: "bg-green-500 hover:bg-green-600 text-white",
  //       summaryButton: "bg-orange-500 hover:bg-orange-600 text-white",
  //       quoteButton: "bg-red-500 hover:bg-red-600 text-white"
  //     };
  //   }
  // };
  // const [vendorStyles, setVendorStyles] = useState(getStylesByVendor());
  

  // Initialize state after user permissions load
  useEffect(() => {
    if (isCheckingAccess) return; // Wait for permissions to finish loading
    
    log('🔧 Initializing user permissions:', { 
      hasSpecialAccess, 
      licenseCheckComplete,
      vendorId: vendor.id,
      forceConsultationMode: vendor.businessRules.forceConsultationMode
    });
    
    // Set initial consultation mode based on vendor requirements and user tier
    setConsultationMode(deriveInitialConsultationMode(
      canToggleConsultation, vendor.businessRules.forceConsultationMode));
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckingAccess]);

  const [showVersionTooltip, setShowVersionTooltip] = useState(false);


  // MORK TODO INVESTIGATE
  // Prepare the configuration object needed for capacity calculations
  const capacityConfig = {
    hybridNodeCount: system.hybrid.nodeCount,
    jbodSize: system.hybrid.jbodSize,
    hybridHddCapacity: system.hybrid.hddCapacity,
    hybridVpodSsdCapacity: system.hybrid.vpodSsdCapacity,
    veloCount: system.director.veloCount,
    veloSsdCapacity: system.director.veloSsdCapacity
  };

  //************************************************************************************
  // Display section                                                  
  //************************************************************************************
  return (
    <>
      {(
        <div className={`space-y-8 p-6 bg-black min-h-screen text-white vendor-${vendor.id.toLowerCase()}`}>
          <div className="flex-row-between">
            <div className="flex-row-spaced">
              <img
                src={vendor.branding.combinedLogo}
                alt="Logo"
                className={vendor.branding.logoClassName}
              />
              <div className="text-white text-xl font-bold">
                {process.env.REACT_APP_TITLE || "V5000 System Configurator"}
                <span className="relative inline-block">
                  <span
                    className="text-xs text-gray-400 ml-2 cursor-help"
                    onMouseEnter={() => setShowVersionTooltip(true)}
                    onMouseLeave={() => setShowVersionTooltip(false)}
                  >
                    v{VERSION}
                  </span>
                  {showVersionTooltip && (
                    <div className="absolute left-0 top-5 z-50 bg-gray-800 border border-gray-600 rounded p-3 text-xs text-gray-200 w-80 shadow-lg">
                      <div className="font-semibold text-white mb-1">What's New in v{VERSION}:</div>
                      <ul className="space-y-0.5">
                        {RECENT_CHANGES.map((change, i) => (
                          <li key={i} className="flex gap-1.5"><span>•</span><span>{change}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </span>
              </div>
            </div>
            <div className="flex-row-spaced">
              <h1>Welcome, {userName}</h1>
              <TryNewLookButton />
              {(hasAdminAccess || userGroups.includes('catalogAuthor')) && (
                <Link
                  to="/admin"
                  className="border-2 border-orange-400 text-orange-400 px-4 py-2 mx-2 rounded hover:bg-orange-400 hover:text-black transition duration-300"
                >
                  Admin
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="border-2 border-white text-white px-4 py-2 rounded hover:bg-white hover:text-black transition duration-300"
              >
                Sign Out
              </button>
            </div>
          </div>
          {/* CONF-106 project C: top-of-page price-edition notices. */}
          {shouldPromptForNewEdition({
            touched: configTouched.current,
            previous: shownEditionRef.current,
            next: priceEditions.editionNumber,
          }) && (
            <div className="bg-orange-900/40 border border-orange-600 text-orange-200 px-4 py-2 flex items-center gap-3">
              <span>New prices available.</span>
              <button className="underline" onClick={() => window.location.reload()}>Reload</button>
            </div>
          )}
          {priceEditions.staleWarning && (
            <div className="bg-yellow-900/40 border border-yellow-600 text-yellow-200 px-4 py-2 flex items-center gap-3">
              <span>Prices may be out of date.</span>
              <button className="underline" onClick={() => priceEditions.refresh()}>Retry</button>
            </div>
          )}
          {/* CONF-106 project C review fix: 'error' means the edition fetch
           * failed AND nothing was cached, so priceBookFromEdition(null, ...)
           * is quietly falling back to the hard-coded price file with no
           * provenance at all — worse than 'stale' (a cached REAL edition),
           * which already warns above. A vendor that legitimately has zero
           * editions never reaches 'error': a successful empty fetch is
           * cached as an answer (kind: 'none') and resolves to 'ready' with
           * edition: null, never touching this branch. */}
          {priceEditions.status === 'error' && (
            <div className="bg-red-900/40 border border-red-600 text-red-200 px-4 py-2 flex items-center gap-3">
              <span>Could not verify current prices. Prices shown may not reflect the latest published edition.</span>
              <button className="underline" onClick={() => priceEditions.refresh()}>Retry</button>
            </div>
          )}
          {/* Hardware Vendor Selection - only show if user has multiple vendors */}
          {hasMultipleVendors && (
            <div className="flex items-center">
              <Select value={vendor.id} onValueChange={handleVendorChange}>
                <SelectTrigger className="w-64 [&>svg]:hidden">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="select-content">
                  {accessibleVendorIds.map((vendorId) => {
                    const vendorConfig = getVendor(vendorId);
                    return (
                      <SelectItem key={vendorId} value={vendorId} className="select-item">
                        <div className="flex-row-tight">
                          <img
                            src={vendorConfig.branding.logo}
                            alt={vendorConfig.displayName}
                            className="h-5 w-12 object-contain"
                            style={vendorConfig.branding.logoStyle}
                          />
                          <span>{vendorConfig.displayName} Hardware</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          <Card>
            <CardHeader className={`${theme.cardHeader} flex-row justify-between py-3`}>
              {/* LEFT: Title + View Buttons together */}
              <div className="flex-row-tight">
                <CardTitle className={`${theme.cardTitle} mr-6`}>Configurations</CardTitle>
                <button
                  onClick={() => {
                      setActiveView('BaseSystem')
                      updateConfig({systemType: 'BaseSystem'})
                    }
                  }
                  className={`px-4 py-2 rounded border ${
                    activeView === 'BaseSystem'
                      ? `${theme.button}`
                      : 'bg-black text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  Base Configuration
                </button>
                <button
                  onClick={() => {
                      setActiveView('Expansion')
                      updateConfig({systemType: 'Expansion'})
                    }
                  }
                  className={`px-4 py-2 rounded border ${
                    activeView === 'Expansion'
                      ? `${theme.button}`
                      : 'bg-black text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  Expansion
                </button>

              </div>
              {/* RIGHT: Consultation toggle (hidden for locked-tier users) */}
              {canToggleConsultation && (
                <Toggle
                  leftLabel="Consultation Mode"
                  rightLabel="Enabled"
                  isRightActive={consultationMode}
                  onToggle={() => setConsultationMode(!consultationMode)}
                  labelClassName={theme.toggleLabelClassName}
                  forceRight={vendor.businessRules.forceConsultationMode}
                />
              )}
            </CardHeader>
          </Card>

          {/* Configuration Controls Card */}

          {/*           */}
          {activeView === 'BaseSystem' && (
            <BaseSystemView
              config={config}
              updateConfig={updateConfig}
              batchUpdateConfig={updateConfig}
              loadConfig={loadConfig}
              metrics={displayMetrics}
              baselineMetrics={metrics}
              affectedKeys={affectedKeys}
              staleKeys={staleKeys}
              pricingData={pricingData}
              vendor={vendor}
              consultationMode={effectiveConsultationMode}
              hasSpecialAccess={hasSpecialAccess}
              hasExecAccess={!isCheckingAccess && hasExecAccess}
              hasDualCpuAccess={!isCheckingAccess && hasDualCpuAccess}
              prices={prices}
              execMetrics={execMetrics}
              showExecMetrics={showExecMetrics}
              onToggleExecMetrics={() => setShowExecMetrics((v) => !v)}
            />
          )}

          {         }
          {activeView === 'Expansion' && (
            <ExpansionSystemView
              config={config}
              updateConfig={updateConfig}
              batchUpdateConfig={updateConfig}
              metrics={displayMetrics}
              baselineMetrics={metrics}
              affectedKeys={affectedKeys}
              staleKeys={staleKeys}
              pricingData={pricingData}
              vendor={vendor}
              consultationMode={effectiveConsultationMode}
              hasSpecialAccess={hasSpecialAccess}
              hasExecAccess={!isCheckingAccess && hasExecAccess}
              hasDualCpuAccess={!isCheckingAccess && hasDualCpuAccess}
              execMetrics={execMetrics}
              showExecMetrics={showExecMetrics}
              onToggleExecMetrics={() => setShowExecMetrics((v) => !v)}
            />
          )} {/**/}

          {/* System Attributes Card */}



          {/* Bill of Materials Card */}
          {!effectiveConsultationMode && (
            <Card>
              <CardHeader className={theme.cardHeader}>
                <div className="flex-row-between">
                  <CardTitle className={theme.cardTitle}>Bill of Materials</CardTitle>
                  {/* RIGHT: exec disclosure + registration toggle, grouped so the
                      header keeps exactly two justify-between children. */}
                  <div className="flex-row-spaced">
                    {!isCheckingAccess && hasExecAccess && (
                      <button
                        type="button"
                        aria-expanded={showExecPricing}
                        aria-controls="exec-pricing-panel"
                        onClick={() => {
                          execPricingTouched.current = true;
                          setShowExecPricing((v) => !v);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 rounded border ${
                          showExecPricing ? `${theme.button}` : 'bg-black text-gray-300 hover:bg-gray-800'
                        }`}
                      >
                        Custom HW Pricing
                        <span className={`text-xs transition-transform ${showExecPricing ? 'rotate-180' : ''}`}>
                          ▾
                        </span>
                      </button>
                    )}
                    {/* MORK TODO : Change to a TOGGLE*/}
                    <Toggle
                      leftLabel="Registered"
                      rightLabel="Un-Registered"
                      isRightActive={!viewState.isNoReg}
                      onToggle={handleRegToggle}
                      labelClassName={theme.toggleLabelClassName}
                      className="flex-row"
                    />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                {showExecPricing && !isCheckingAccess && hasExecAccess && (
                  <div id="exec-pricing-panel">
                    <ExecPricingView
                      execPricing={execPricing}
                      productDb={priceBook}
                      config={config}
                      activePartNumbers={new Set(getAllUniquePartNumbers(config, priceBook, metrics, vendor) || [])}
                      onReprice={startReprice}
                    />
                  </div>
                )}
                <div className="border rounded-lg overflow-hidden">
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow className={theme.tableHeader}>
                        <TableHead className="table-head">P/N</TableHead>
                        <TableHead className="table-head">Description</TableHead>
                        <TableHead className="table-head">Node</TableHead>
                        <TableHead className="table-head">Quantity</TableHead>
                        <TableHead className="table-head">Months</TableHead>
                        <TableHead className="table-head">List Price</TableHead>
                        <TableHead className="table-head">Discount</TableHead>
                        <TableHead className="table-head">Discounted Price</TableHead>
                        <TableHead className="table-head">Extended Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bom.filter(item => item.showInUi !== false).map((item, index) => {
                        // SW/HW section divider
                        if (item.item === '__sw_hw_separator__') {
                          return (
                            <TableRow key={index}>
                              <TableCell colSpan="9" className="p-0">
                                <div className="border-t-2 border-gray-400 mx-2" />
                              </TableCell>
                            </TableRow>
                          );
                        }

                        // Special rendering for Solution Price row
                        if (item.item === 'Solution Price') {
                          return (
                            <TableRow key={index} className="hover:bg-gray-100/40">
                              <TableCell colSpan="8" className="text-right font-bold pr-4">
                                {softwareSubscriptionOnly ? 'VDURA Pricing' : 'Solution Price'}
                              </TableCell>
                              <TableCell className={`text-center font-bold${bom.some(r => r.isCustomPrice) ? ' text-red-500' : ''}`}>
                                {item.totalCost !== undefined
                                  ? `$${Number(item.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  : ""}
                              </TableCell>
                            </TableRow>
                          );
                        }

                        // Normal row rendering
                        return (
                          <TableRow key={index} className="hover:bg-gray-100/40">
                            <TableCell className="font-medium">{item.partNumber}</TableCell>
                            <TableCell className="font-medium">{item.item}</TableCell>
                            <TableCell className="font-medium">{item.node}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-center font-medium">{item.months}</TableCell>
                            <TableCell className={`text-center${item.isCustomPrice ? ' text-red-500 font-bold' : ''}`}>
                              {item.unitCost !== undefined
                                ? `$${Number(item.list).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : ""}
                            </TableCell>
                            <TableCell className="text-center">{item.discount}</TableCell>
                            <TableCell className={`text-center${item.isCustomPrice ? ' text-red-500 font-bold' : ''}`}>
                              {item.unitCost !== undefined
                                ? `$${Number(item.unitCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : ""}
                            </TableCell>
                            <TableCell className={`text-center font-semibold${item.isCustomPrice ? ' text-red-500 font-bold' : ''}`}>
                              {item.totalCost !== undefined
                                ? `$${Number(item.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : ""}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hardware Reference Card - partner-fulfilled hardware, mirrors the quote PDF section */}
          {!effectiveConsultationMode && softwareSubscriptionOnly && (
            <Card>
              <CardHeader className={theme.cardHeader}>
                <CardTitle className={theme.cardTitle}>
                  Hardware Reference: Fulfilled by Contract Manufacturer
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="border rounded-lg overflow-hidden">
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow className={theme.tableHeader}>
                        <TableHead className="table-head">Node Description</TableHead>
                        <TableHead className="table-head">Node Qty</TableHead>
                        <TableHead className="table-head">Storage Device Capacity (TB)</TableHead>
                        <TableHead className="table-head">Storage Device Qty</TableHead>
                        <TableHead className="table-head">Total Raw Capacity (TB)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {buildHardwareReferenceRows(config, metrics, vendor.displayName).map((row, index) => (
                        <TableRow key={index} className="hover:bg-gray-100/40">
                          <TableCell className="font-medium">{row.nodeDescription}</TableCell>
                          <TableCell className="text-center">{row.nodeQty}</TableCell>
                          <TableCell className="text-center">{row.deviceCapacityTB}</TableCell>
                          <TableCell className="text-center">{row.deviceQty}</TableCell>
                          <TableCell className="text-center">
                            {Number(row.totalRawTB).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Special Pricing Card with conditional rendering */}
          {!effectiveConsultationMode && !isCheckingAccess && hasSpecialAccess && (
            <Card>
              <CardHeader className={theme.cardHeader}>
                <CardTitle className={theme.cardTitle}>Special Pricing</CardTitle>
              </CardHeader>
              <CardContent className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4 text-white">
                <div>
                  <label>{swModel.termBased ? 'SSD Software Price ($ / 10TB / month)' : 'SSD Software Price ($)'}</label>
                  <input
                    type="number"
                    value={tempSsdSoftware} // Use the temporary state for rendering
                    step="0.1"
                    onChange={handleSsdSoftwareChange}
                    onBlur={handleSsdSoftwareBlur} // Add onBlur handler
                    className="input-box"
                    placeholder="Enter SSD Software Price"
                  />
                  {swModel.termBased && (
                    <p className="text-xs mt-1 opacity-70">
                      = ${Number(pricing.baseSsdSoftware).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per TB for the {swModelMonths}-month term
                    </p>
                  )}
                </div>
                <div>
                  <label>{swModel.termBased ? 'HDD Software Price ($ / 10TB / month)' : 'HDD Software Price ($)'}</label>
                  <input
                    type="number"
                    value={tempHddSoftware} // Use the temporary state for rendering
                    step="0.1"
                    onChange={handleHddSoftwareChange}
                    onBlur={handleHddSoftwareBlur} // Add onBlur handler
                    className="input-box"
                    placeholder="Enter HDD Software Price"
                  />
                  {swModel.termBased && (
                    <p className="text-xs mt-1 opacity-70">
                      = ${Number(pricing.baseHddSoftware).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per TB for the {swModelMonths}-month term
                    </p>
                  )}
                </div>

                <div>
                  <label>Discount Months</label>
                  <input
                    type="number"
                    value={tempDiscountMonths}
                    onChange={handleDiscountMonthsChange}
                    onBlur={handleDiscountMonthsBlur}
                    className="input-box"
                    placeholder="Enter Discount Months"
                  />
                </div>
                <SpecialPricingMetrics
                  pricingData={pricingData}
                  baselinePricing={baselinePricing}
                  metrics={displayMetrics}
                  softwareSubscriptionOnly={softwareSubscriptionOnly}
                />
              </CardContent>
            </Card>
          )}

          {/* Pricing Card for non special pricing*/}
          {!effectiveConsultationMode && !isCheckingAccess && !hasSpecialAccess && (
            <Card>
              <CardHeader className={theme.cardHeader}>
                <CardTitle className={theme.cardTitle}>Pricing</CardTitle>
              </CardHeader>
              <CardContent className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4 text-white">

                <div>
                  <p className="metric-label">Dollars per Raw / Useable / Effective TB</p>
                  <p className="metric-value">
                    ${displayMetrics.dollarsPerRawTB.toFixed(2)} / $
                    {displayMetrics.dollarsPerEffectiveTB.toFixed(2)} / $
                    {displayMetrics.dollarsPerCompressedEffectiveTB.toFixed(2)}
                    <span>  TB</span>
                  </p>
                </div>

                <div>
                  {!softwareSubscriptionOnly && <>
                    <p className="metric-label">VDURA Care + Install %</p>
                    <p className="metric-value">{Number((pricingData?.solution.softwareTotal/pricingData?.solution.grandTotal * 100) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} %</p>
                  </>}
                </div>
                <div>
                  <p className="metric-label">{softwareSubscriptionOnly ? 'VDURA Pricing' : 'Solution Price'}</p>
                  <p className="metric-value">${Number(softwareSubscriptionOnly ? (pricingData?.solution?.softwareTotal || 0) : (pricingData?.solution?.grandTotal || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              </CardContent>
            </Card>
          )}
          {!effectiveConsultationMode && (
            <button
              onClick={handleGenerateSystemPDF}
              className="px-4 py-2 mx-2 bg-orange-500 text-white rounded"
            >
              Summary PDF
            </button>
          )}
          <button
            onClick={handleGenerateRackSlide}
            className="px-4 py-2 mx-2 bg-purple-500 text-white rounded"
          >
            System Slide
          </button>
          <button
            onClick={() => setShowSaveDialog(true)}
            className="mx-2 px-4 py-2 bg-blue-500 text-white rounded"
          >
            Save Configuration
          </button>
          <button
            onClick={() => setShowQuotesModal(true)}
            className="mx-2 px-4 py-2 bg-green-500 text-white rounded">
            Load Configuration
          </button>
          <SaveQuoteDialog
            isOpen={showSaveDialog}
            onClose={() => setShowSaveDialog(false)}
            onSave={saveQuote}
          />
          <QuotesModal
            isOpen={showQuotesModal}
            onClose={() => setShowQuotesModal(false)}
            onLoadConfig={loadSavedConfig}
            userEmail={userName}
            hasFullConfigurator={hasFullConfigurator}
          />
          <ExportToS3Dialog
            isOpen={showExportDialog}
            onClose={() => setShowExportDialog(false)}
            bom={bom}
            config={config}
            metrics={displayMetrics}
            opportunityID={metadata.opportunityID}
            customerNumber={metadata.customerNumber}
            offerNumber={metadata.offerNumber}
            preparedFor={metadata.preparedFor}
            onBeforeExport={async (filename) => !execEscapeActive() || logEscape('csvExport', { quoteSerial: filename })}
          />
          <div className="space-y-8 p-6 bg-black min-h-screen text-white">


            {!effectiveConsultationMode && hasSpecialAccess && (
              <Card className="w-full max-w-none">
                <CardHeader className={theme.cardHeader}>
                  <div className="flex items-center justify-between">
                    <CardTitle className={theme.cardTitle}>Quote Details</CardTitle>
                    {/* CCI quote path — AIC-only, specialPricing-only; opt-out (defaults to CCI). Kill switch: CCI_FEATURE_ENABLED */}
                    {CCI_FEATURE_ENABLED && vendor.id === 'AIC' && (
                      // CONF-210: disabled with the reason while the flash set ships
                      // Dual CPU nodes, which no CCI bundle covers.
                      <span title={cciBlockedByDualCpuReason(config) || undefined}>
                        <Toggle
                          leftLabel="Standard"
                          rightLabel="CCI"
                          isRightActive={viewState.cciEnabled === true}
                          onToggle={() => updateConfig({ cciEnabled: !viewState.cciEnabled })}
                          labelClassName={theme.toggleLabelClassName}
                          disabled={Boolean(cciBlockedByDualCpuReason(config))}
                        />
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 text-white">
                  {/* Account Executive Dropdown*/}
                  <div>
                    <label>Account Executive *</label>
                    <Select
                      value={metadata.accountExecutive}
                      onValueChange={(val) => {
                        handleAccountExecutiveChange(val);
                        setFieldError('accountExecutive', val);
                      }}
                    >
                      <SelectTrigger className={`${theme.selectTrigger} ${quoteErrors.accountExecutive ? 'input-error' : ''}`}>
                        <SelectValue placeholder="Select Account Executive" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        {accountExecutives.map((exec) => (
                          <SelectItem
                            key={exec.email}
                            value={exec.name}
                            className={theme.selectItem}
                          >
                            {exec.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {quoteErrors.accountExecutive && <p className="input-error-message">{quoteErrors.accountExecutive}</p>}
                  </div>

                  {/* Opportunity ID */}
                  <div>
                    <label>Opportunity ID (18 digits) *</label>
                    <input
                      type="text"
                      value={metadata.opportunityID}
                      onChange={(e) => updateConfig({ opportunityID: e.target.value })}
                      onBlur={(e) => setFieldError('opportunityID', e.target.value)}
                      maxLength={18}
                      className={`input-box ${quoteErrors.opportunityID ? 'input-error' : ''}`}
                      placeholder="Enter 18-digit Opportunity ID"
                    />
                    {quoteErrors.opportunityID && <p className="input-error-message">{quoteErrors.opportunityID}</p>}
                  </div>

                  {/* Quote Description */}
                  <div>
                    <label>Quote Description (Shown on Save/Load menu)</label>
                    <input
                      type="text"
                      value={metadata.quoteDescription}
                      onChange={(e) => updateConfig({ quoteDescription: e.target.value })}
                      className="input-box"
                      placeholder="Enter quote description"
                    />
                  </div>

                  {/* Customer Name (formerly "Prepared For") MORK TODO : Rename and remove preparedFor from config*/}
                  <div>
                    <label>Customer Name *</label>
                    <input
                      type="text"
                      value={metadata.preparedFor}
                      onChange={(e) => updateConfig({ preparedFor: e.target.value })}
                      onBlur={(e) => setFieldError('preparedFor', e.target.value)}
                      className={`input-box ${quoteErrors.preparedFor ? 'input-error' : ''}`}
                      placeholder="Enter customer name"
                    />
                    {quoteErrors.preparedFor && <p className="input-error-message">{quoteErrors.preparedFor}</p>}
                  </div>

                  {/* Salesforce Quote Number */}
                  <div>
                    <label>Salesforce Quote # *</label>
                    <input
                      type="text"
                      value={metadata.salesforceQuoteNumber}
                      onChange={(e) => updateConfig({ salesforceQuoteNumber: e.target.value })}
                      onBlur={(e) => setFieldError('salesforceQuoteNumber', e.target.value)}
                      className={`input-box ${quoteErrors.salesforceQuoteNumber ? 'input-error' : ''}`}
                      placeholder="Enter Salesforce quote number"
                    />
                    {quoteErrors.salesforceQuoteNumber && <p className="input-error-message">{quoteErrors.salesforceQuoteNumber}</p>}
                  </div>

                  {/* Notes */}
                  <div>
                    <label>Notes (Displayed in Quote Notes section)</label>
                    <input
                      type="text"
                      value={metadata.notes}
                      onChange={(e) => updateConfig({ notes: e.target.value })}
                      className="input-box"
                      placeholder="Enter notes"
                    />
                  </div>

                  {/* Partner Name (Optional) */}
                  <div>
                    <label>Partner Name (Optional)</label>
                    <input
                      type="text"
                      value={metadata.partnerName}
                      onChange={(e) => updateConfig({ partnerName: e.target.value })}
                      className="input-box"
                      placeholder="Enter partner name"
                    />
                  </div>

                  {/* Warranty Start Date (Optional) */}
                  {hasSpecialAccess && (
                    <div>
                      <label>Warranty Start Date (Optional)</label>
                      <input
                        type="date"
                        value={metadata.warrantyStartDate}
                        onChange={(e) => updateConfig({ warrantyStartDate: e.target.value })}
                        className="input-box"
                        placeholder=""
                      />
                    </div>
                  )}

                  {/* Quote Expiration Date */}
                  {/* MORK TODO : Only Special Access can currently generate non default expiration dates (today + 30 days) There needs to be some sort of admin/special separation */}
                  <div>
                    <label>Quote Expires</label>
                    <input
                      type="date"
                      value={metadata.quoteExpirationDate || calculateDefaultExpirationDate()}
                      onChange={(e) => updateConfig({ quoteExpirationDate: e.target.value })}
                      disabled={!hasSpecialAccess}
                      className="input-box"
                    />
                  </div>
                </CardContent>
                <CardContent>
                  {/* Only show the Quote PDF button for special access users */}
                  {!effectiveConsultationMode && hasSpecialAccess && (
                    <button
                      onClick={handleGenerateQuotePDF}
                      className="px-4 py-2 bg-red-500 text-white rounded"
                    >
                      Generate Quote PDF
                    </button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Hidden rack capture target for PPTX export */}
            {/* <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '500px', pointerEvents: 'none' }}>
              <div ref={rackCaptureRef}>
                <RackCaptureVisualization config={config} productDb={productDb} />
              </div>
            </div> */}

            <div className="mt-8 border-t border-gray-700 pt-4 text-center text-sm text-gray-500">
              VDURA Confidential and Proprietary, NDA required. 2026
            </div>
          </div>
        </div>


      )}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <BusyOverlay label={busy} />
      {execConfirmElement}
      {repriceDiff && (
        <RepriceDialog
          diff={repriceDiff.diff}
          qtyByPn={qtyByPartNumber}
          basis={{
            vendor: execPricing.snapshot?.vendor ?? null,
            quarter: execPricing.snapshot?.quarter ?? null,
            catalogIntegrationBp: execPricing.snapshot?.integrationBp ?? null,
            overrideIntegrationBp: execPricing.overrides?.integrationBp ?? null,
            customSkuCount: execPricing.customSkus?.size ?? 0,
            catalogSkuCount: Object.keys(execPricing.snapshot?.compositions ?? {}).length,
          }}
          onAccept={acceptReprice}
          onCancel={() => setRepriceDiff(null)}
        />
      )}
    </>

  );

};

//************************************************************************************
// Main App component
//************************************************************************************
const App = () => {
  return (
    <AuthWrapper>
      {({ user, signOut }) => (
        <BrowserRouter>
          <Routes>
            {/* ConfiguratorGate wraps LicenseGate, not the reverse: a user with
                no vendor group sees the Home page without an NDA click-through
                (nothing on it is confidential), and neither shell mounts for
                them. Everyone else gets the license wall, then the shell seeded
                with the vendor to start on.
                CONF-201: `/` serves V2. Classic stays mounted at `/classic` (the
                V2 top bar's "Back to classic" link) until it is retired, and
                `/v2` stays as an alias so existing bookmarks keep working. */}
            <Route
              path="/"
              element={(
                <ConfiguratorGate user={user} signOut={signOut}>
                  {({ initialVendorId }) => (
                    <LicenseGate user={user}>
                      <AppShell user={user} signOut={signOut} initialVendorId={initialVendorId} />
                    </LicenseGate>
                  )}
                </ConfiguratorGate>
              )}
            />
            <Route
              path="/classic"
              element={(
                <ConfiguratorGate user={user} signOut={signOut}>
                  {({ initialVendorId }) => (
                    <LicenseGate user={user}>
                      <StorageConfiguratorApp user={user} signOut={signOut} initialVendorId={initialVendorId} />
                    </LicenseGate>
                  )}
                </ConfiguratorGate>
              )}
            />
            <Route
              path="/v2"
              element={(
                <ConfiguratorGate user={user} signOut={signOut}>
                  {({ initialVendorId }) => (
                    <LicenseGate user={user}>
                      <AppShell user={user} signOut={signOut} initialVendorId={initialVendorId} />
                    </LicenseGate>
                  )}
                </ConfiguratorGate>
              )}
            />
            {adminRoutes(user)}
            {process.env.NODE_ENV !== 'production' && (
              <Route path="/dev/kit" element={<DevKit />} />
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      )}
    </AuthWrapper>
  );
};


export default App;